import React, { useState } from 'react';
import { toLower } from 'lodash';
import { formatNumericDate, getTimeInHoursAndMins } from '../../utils/dates';
import Table from './Table';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

const initialData = [{
  id: 1,
  organization_id: 1,
  name: 'Route A',
  description: 'Long Route',
  date: '2022-05-01',
  number_of_stops: 2,
  total_distance: 1040.5,
  total_time: 57924,
},
{
  id: 2,
  organization_id: 1,
  name: 'Route B',
  description: 'Short Route',
  date: '2022-04-01',
  number_of_stops: 1,
  total_distance: 3000,
  total_time: 20000,
},
{
  id: 3,
  organization_id: 1,
  name: 'Route C',
  description: 'Mini Route',
  date: '2022-03-01',
  number_of_stops: 0,
  total_distance: 500,
  total_time: null,
},
{
  id: 4,
  organization_id: 1,
  name: 'Route D',
  description: 'Crazy Route',
  date: '2022-02-01',
  number_of_stops: 8,
  total_distance: null,
  total_time: 40000,
},
{
  id: 5,
  organization_id: 1,
  name: 'Route E',
  description: 'Vacation Route',
  date: '2022-01-01',
  number_of_stops: 6,
  total_distance: 5,
  total_time: null,
}];
const columns = [
  {
    title: 'Name', dataIndex: 'name', className: 'inline-flex w-96', isSortable: true,
  },
  {
    title: 'Date', dataIndex: 'date', render: (text) => formatNumericDate(text), className: 'inline-flex w-32', isSortable: true,
  },
  {
    title: 'Sites', dataIndex: 'number_of_stops', className: 'inline-flex w-32', isSortable: true,
  },
  {
    title: 'Distance', dataIndex: 'total_distance', render: (text) => text && `${(text).toFixed(2)} mi`, className: 'inline-flex w-32', isSortable: true,
  },
  {
    title: 'Time', dataIndex: 'total_time', render: (text) => getTimeInHoursAndMins(text), className: 'inline-flex w-32', isSortable: true,
  },
];

export default {
  title: 'Components/Table',
  component: Table,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `Table` component provides a dynamic and customizable table structure for displaying and interacting with tabular data. It supports sorting, expandable rows, and custom cell rendering.\n\n',
      // When & how to use it?
      '\nUse the `Table` component for displaying structured data in a grid format with interactive capabilities like sorting and expandable rows. It is ideal for use cases like managing lists, viewing details, and organizing datasets.\n\n',
      // Current Usage
      `\nThe \`Table\` component is widely used across pages for displaying:
      \n- Routes
      \n- Sites
      \n- Contacts
      \n- Notes\n\n`,
      // Related Components
      '\n- **[Icons](http://localhost:6006/?path=/docs/components-icons-icons--docs)**: Used for displaying sort icons.\n\n',
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    dataSource: {
      control: { type: 'array' },
      description: 'The array of data objects to be displayed in the table rows.\n\n',
      table: {
        type: { summary: 'array' },
        required: true,
      },
    },
    columns: {
      control: { type: 'array' },
      description: 'Defines the columns of the table, including titles, data indices, and custom renderers.\n\n',
      table: {
        type: { summary: 'array' },
        required: true,
      },
    },
    onSort: {
      control: { type: 'function' },
      description: 'Callback function triggered when a sortable column header is clicked. Receives an object with field and order.\n\n',
      table: {
        type: { summary: 'function' },
        required: false,
      },
    },
    typeOfData: {
      control: { type: 'text' },
      description: 'Defines the type of data displayed in the table, used for generating navigation URLs.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    isHistory: {
      control: { type: 'boolean' },
      description: 'Indicates whether the table is used for displaying history data.\n\n',
      table: {
        type: { summary: 'boolean' },
        required: false,
      },
    },
    className: {
      control: { type: 'text' },
      description: 'Custom CSS classes for styling the table.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
  },
};

export const Routes = () => {
  const [data, setData] = useState(initialData);

  const sortData = (field, order) => {
    const newData = [...data];
    if (field === 'name') {
      newData.sort((a, b) => (
        order === 'asc'
          ? b[field].localeCompare(a[field])
          : a[field].localeCompare(b[field])
      ));
    } else if (field === 'date') {
      newData.sort((a, b) => {
        const dateA = new Date(a[field]);
        const dateB = new Date(b[field]);
        if (order === 'asc') {
          return dateA - dateB;
        }
        return dateB - dateA;
      });
    } else {
      newData.sort((a, b) => (
        order === 'asc'
          ? toLower(b[field]) - toLower(a[field])
          : toLower(a[field]) - toLower(b[field])
      ));
    }

    setData(newData);
  };

  return (
    <Table
      columns={columns}
      dataSource={data}
      onSort={(values) => sortData(values.field, values.order)}
    />
  );
};

Routes.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant demonstrates the `Table` component for displaying routes data with sortable columns.\n\n',
  // Where is it currently used on the product?
  '\nThe `Table` component is used on the Routes page to manage route information.\n\n',
);
